/**
 * Applies the tenant's primary colour to the brand token scale at runtime (docs/design/15 §1).
 * Falls back to the default blue when the colour would not give readable white-on-brand text.
 */
const DEFAULT = '#2563EB';

function luminance(hex: string): number {
  const [r, g, b] = [1, 3, 5]
    .map((i) => parseInt(hex.slice(i, i + 2), 16) / 255)
    .map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastWithWhite(hex: string): number {
  return 1.05 / (luminance(hex) + 0.05);
}

export function safeBrandColor(hex: string | null | undefined): string {
  if (!hex || !/^#[0-9A-Fa-f]{6}$/.test(hex)) return DEFAULT;
  return contrastWithWhite(hex) >= 4.5 ? hex : DEFAULT;
}

const STEPS: [string, string][] = [
  ['50', 'color-mix(in oklab, var(--brand-base) 8%, white)'],
  ['100', 'color-mix(in oklab, var(--brand-base) 16%, white)'],
  ['200', 'color-mix(in oklab, var(--brand-base) 32%, white)'],
  ['300', 'color-mix(in oklab, var(--brand-base) 50%, white)'],
  ['400', 'color-mix(in oklab, var(--brand-base) 75%, white)'],
  ['500', 'color-mix(in oklab, var(--brand-base) 90%, white)'],
  ['600', 'var(--brand-base)'],
  ['700', 'color-mix(in oklab, var(--brand-base) 85%, black)'],
  ['800', 'color-mix(in oklab, var(--brand-base) 70%, black)'],
  ['900', 'color-mix(in oklab, var(--brand-base) 55%, black)'],
];

export function applyBrand(hex: string | null | undefined): void {
  const root = document.documentElement;
  if (!hex) {
    root.style.removeProperty('--brand-base');
    for (const [step] of STEPS) root.style.removeProperty(`--brand-${step}`);
    return;
  }
  root.style.setProperty('--brand-base', safeBrandColor(hex));
  for (const [step, value] of STEPS) root.style.setProperty(`--brand-${step}`, value);
}
